import React, { useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";
import VitalSignsRecord from "./VitalSignsRecord";
import VitalSignsHistory from "./VitalSignsHistory";

const API_BASE =
  (process.env.REACT_APP_API_URL || "").includes("undefined")
    ? "http://localhost:8000/api"
    : (process.env.REACT_APP_API_URL || "http://localhost:8000/api");
const VitalSignsPage = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [recordOpen, setRecordOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearch, setPatientSearch] = useState("");
  const [tabValue, setTabValue] = useState(0);

  const {
    data: patientsData,
    isLoading: patientsLoading,
  } = useQuery({
    queryKey: ["nurse-patients"],
    queryFn: () =>
      axios
        .get(`${API_BASE}/nurse-patients/`, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => res.data),
    enabled: !!token,
  });

  const {
    data: vitalSignsData,
    isLoading: vitalSignsLoading,
    isError: vitalSignsError,
  } = useQuery({
    queryKey: ["vital-signs", selectedPatient?.id],
    queryFn: () =>
      axios
        .get(
          `${API_BASE}/vital-signs/?patient=${selectedPatient?.id}`,
          {
            headers: { Authorization: `Token ${token}` },
          }
        )
        .then((res) => res.data),
    enabled: !!token && !!selectedPatient?.id,
  });

  const allPatients = [
    ...(patientsData?.ipd_patients || []),
    ...(patientsData?.icu_patients || []),
  ];

  const vitalSigns = vitalSignsData?.results || vitalSignsData || [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5">
              {t("nurse.vitalSigns", "Vital Signs")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setRecordOpen(true)}
            >
              {t("nurse.recordVitalSigns", "Record Vital Signs")}
            </Button>
          </Box>

          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label={t("nurse.record", "Record")} />
            <Tab label={t("nurse.history", "History")} />
            <Tab label={t("nurse.trends", "Trends")} />
          </Tabs>

          {tabValue === 0 && (
            <>
              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  options={allPatients}
                  getOptionLabel={(option) => option.patient_name || ""}
                  value={selectedPatient}
                  onChange={(event, newValue) => {
                    setSelectedPatient(newValue);
                  }}
                  inputValue={patientSearch}
                  onInputChange={(event, newInputValue) => {
                    setPatientSearch(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("nurse.selectPatient", "Select Patient")}
                      placeholder={t("nurse.searchPatient", "Search patient...")}
                    />
                  )}
                />
              </Box>

              {selectedPatient && (
                <>
                  {vitalSignsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : vitalSignsError ? (
                    <Alert severity="error">
                      {t("nurse.errorLoadingVitalSigns", "Failed to load vital signs.")}
                    </Alert>
                  ) : vitalSigns.length === 0 ? (
                    <Alert severity="info">
                      {t("nurse.noVitalSigns", "No vital signs recorded for this patient.")}
                    </Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {vitalSigns.map((vs) => (
                        <Grid item xs={12} md={6} lg={4} key={vs.id}>
                          <VitalSignsCard vitalSigns={vs} t={t} />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}

              {!selectedPatient && (
                <Alert severity="info">
                  {t("nurse.selectPatientToViewVitalSigns", "Please select a patient to view vital signs history.")}
                </Alert>
              )}
            </>
          )}

          {tabValue === 1 && (
            <>
              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  options={allPatients}
                  getOptionLabel={(option) => option.patient_name || ""}
                  value={selectedPatient}
                  onChange={(event, newValue) => {
                    setSelectedPatient(newValue);
                  }}
                  inputValue={patientSearch}
                  onInputChange={(event, newInputValue) => {
                    setPatientSearch(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("nurse.selectPatient", "Select Patient")}
                      placeholder={t("nurse.searchPatient", "Search patient...")}
                    />
                  )}
                />
              </Box>

              {selectedPatient && (
                <VitalSignsHistory patient={selectedPatient} />
              )}

              {!selectedPatient && (
                <Alert severity="info">
                  {t("nurse.selectPatientToViewHistory", "Please select a patient to view vital signs history.")}
                </Alert>
              )}
            </>
          )}

          {tabValue === 2 && (
            <VitalSignsHistory />
          )}
        </Paper>
      </Grid>

      <VitalSignsRecord
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        patient={selectedPatient}
      />
    </Grid>
  );
};

const VitalSignsCard = ({ vitalSigns, t }) => {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {moment(vitalSigns.recorded_at).format("YYYY-MM-DD HH:mm")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("nurse.recordedBy", "Recorded by")}: {vitalSigns.recorded_by_display}
        </Typography>

        <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          {vitalSigns.temperature && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("nurse.temperature", "Temp")}:
              </Typography>
              <Typography variant="body2">{vitalSigns.temperature}°C</Typography>
            </Box>
          )}
          {vitalSigns.heart_rate && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("nurse.heartRate", "HR")}:
              </Typography>
              <Typography variant="body2">{vitalSigns.heart_rate} bpm</Typography>
            </Box>
          )}
          {vitalSigns.blood_pressure_systolic && vitalSigns.blood_pressure_diastolic && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("nurse.bloodPressure", "BP")}:
              </Typography>
              <Typography variant="body2">
                {vitalSigns.blood_pressure_systolic}/{vitalSigns.blood_pressure_diastolic} mmHg
              </Typography>
            </Box>
          )}
          {vitalSigns.respiratory_rate && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("nurse.respiratoryRate", "RR")}:
              </Typography>
              <Typography variant="body2">{vitalSigns.respiratory_rate} bpm</Typography>
            </Box>
          )}
          {vitalSigns.oxygen_saturation && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("nurse.oxygenSaturation", "SpO2")}:
              </Typography>
              <Typography variant="body2">{vitalSigns.oxygen_saturation}%</Typography>
            </Box>
          )}
          {vitalSigns.blood_glucose && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("nurse.bloodGlucose", "Glucose")}:
              </Typography>
              <Typography variant="body2">{vitalSigns.blood_glucose} mg/dL</Typography>
            </Box>
          )}
          {vitalSigns.pain_score !== null && vitalSigns.pain_score !== undefined && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("nurse.painScore", "Pain")}:
              </Typography>
              <Typography variant="body2">{vitalSigns.pain_score}/10</Typography>
            </Box>
          )}
        </Box>

        {vitalSigns.notes && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t("nurse.notes", "Notes")}:
            </Typography>
            <Typography variant="body2">{vitalSigns.notes}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default VitalSignsPage;

